import { useState, useEffect } from 'react';
import { ticketService, TicketCreate } from '../../services/ticketService';
import { uploadService } from '../../services/uploadService';
import { track } from '../../utils/analytics';
import { AnalyticsEvents } from '../../utils/analyticsEvents';
import type { Ticket } from '../../services/ticketService';
import type { SupportNotificationMessage } from './supportTypes';

export function useSupportPage() {
  const [ticketFormData, setTicketFormData] = useState<TicketCreate>({
    title: '',
    description: '',
    image_url: undefined,
    issue_type: undefined,
    priority: 'medium'
  });
  const [customIssueType, setCustomIssueType] = useState<string>('');
  const [ticketImageFiles, setTicketImageFiles] = useState<File[]>([]);
  const [ticketImagePreviews, setTicketImagePreviews] = useState<string[]>([]);
  const [ticketImageNames, setTicketImageNames] = useState<string[]>([]);
  const [uploadingTicketImage, setUploadingTicketImage] = useState(false);
  const [ticketLoading, setTicketLoading] = useState(false);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [ticketMessage, setTicketMessage] = useState<SupportNotificationMessage | null>(null);

  const loadTickets = async () => {
    try {
      setLoadingTickets(true);
      const data = await ticketService.getMyTickets();
      setTickets(data);
    } catch (error: any) {
      console.error('Error loading tickets:', error);
    } finally {
      setLoadingTickets(false);
    }
  };

  useEffect(() => {
    loadTickets();
  }, []);

  useEffect(() => {
    if (ticketMessage) {
      const timer = setTimeout(() => setTicketMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [ticketMessage]);

  const getFilenameFromUrl = (url: string): string => {
    try {
      const urlObj = new URL(url.startsWith('/api/') ? `${process.env.REACT_APP_API_URL || ''}${url}` : url);
      const pathname = urlObj.pathname;
      const filename = pathname.split('/').pop() || 'image';
      return filename.length > 30 ? filename.substring(0, 30) + '...' : filename;
    } catch {
      const parts = url.split('/');
      const filename = parts[parts.length - 1] || 'image';
      return filename.length > 30 ? filename.substring(0, 30) + '...' : filename;
    }
  };

  const handleTicketImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      const validFiles: File[] = [];
      const invalidFiles: string[] = [];
      files.forEach((file) => {
        if (!file.type.startsWith('image/') || file.size > 10 * 1024 * 1024) {
          invalidFiles.push(file.name);
          return;
        }
        validFiles.push(file);
      });
      if (invalidFiles.length > 0) {
        setTicketMessage({ type: 'error', text: 'Some files are invalid. Please select only image files under 10MB each.' });
      }
      if (validFiles.length > 0) {
        const newPreviews: string[] = [];
        const newNames: string[] = [];
        let loadedCount = 0;
        validFiles.forEach((file) => {
          newNames.push(file.name);
          const reader = new FileReader();
          reader.onloadend = () => {
            newPreviews.push(reader.result as string);
            loadedCount++;
            if (loadedCount === validFiles.length) {
              setTicketImagePreviews(prev => [...prev, ...newPreviews]);
              setTicketImageNames(prev => [...prev, ...newNames]);
            }
          };
          reader.readAsDataURL(file);
        });
        setTicketImageFiles(prev => [...prev, ...validFiles]);
      }
    }
  };

  const removeTicketImage = (index: number) => {
    const preview = ticketImagePreviews[index];
    const newPreviews = ticketImagePreviews.filter((_, i) => i !== index);
    const newNames = ticketImageNames.filter((_, i) => i !== index);
    setTicketImagePreviews(newPreviews);
    setTicketImageNames(newNames);
    if (preview.startsWith('data:')) {
      let dataUrlCount = 0;
      for (let i = 0; i < index; i++) {
        if (ticketImagePreviews[i].startsWith('data:')) dataUrlCount++;
      }
      setTicketImageFiles(prev => prev.filter((_, i) => i !== dataUrlCount));
    }
    if (newPreviews.length === 0) {
      setTicketFormData(prev => ({ ...prev, image_url: undefined }));
    }
  };

  const handleTicketSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTicketLoading(true);
    setTicketMessage(null);
    try {
      let imageUrls: string[] = [];
      const existingUrls = ticketImagePreviews
        .filter(preview => preview.startsWith('http://') || preview.startsWith('https://') || preview.startsWith('/api/'))
        .map(url => url.trim());
      imageUrls.push(...existingUrls);
      if (ticketImageFiles.length > 0) {
        setUploadingTicketImage(true);
        const uploadedUrls = await Promise.all(
          ticketImageFiles.map(file => uploadService.uploadImage(file))
        );
        imageUrls.push(...uploadedUrls);
        setUploadingTicketImage(false);
      }
      const imageUrl = imageUrls.length > 0 ? imageUrls.join(',') : undefined;
      const submitData: TicketCreate = {
        ...ticketFormData,
        image_url: imageUrl,
        issue_type: (ticketFormData.issue_type === 'other' && customIssueType.trim())
          ? (customIssueType.trim() as any)
          : ticketFormData.issue_type
      };
      await ticketService.createTicket(submitData);
      setTicketMessage({ type: 'success', text: 'Ticket created successfully!' });
      track(AnalyticsEvents.SUPPORT_TICKET_CREATED);
      setTicketFormData({ title: '', description: '', image_url: undefined, issue_type: undefined, priority: 'medium' });
      setCustomIssueType('');
      setTicketImageFiles([]);
      setTicketImagePreviews([]);
      setTicketImageNames([]);
      setShowTicketForm(false);
      await loadTickets();
    } catch (error: any) {
      setTicketMessage({ type: 'error', text: error.message || 'Failed to create ticket' });
      setUploadingTicketImage(false);
    } finally {
      setTicketLoading(false);
    }
  };

  const closeTicketForm = () => {
    setShowTicketForm(false);
    setTicketFormData({ title: '', description: '', image_url: undefined, issue_type: undefined, priority: 'medium' });
    setCustomIssueType('');
    setTicketImageFiles([]);
    setTicketImagePreviews([]);
    setTicketImageNames([]);
  };

  const formatIssueType = (issueType: string | undefined): string => {
    if (!issueType) return '';
    const predefinedTypes = ['credits', 'subscription', 'billing', 'technical', 'other'];
    if (predefinedTypes.includes(issueType.toLowerCase())) {
      return issueType === 'other' ? 'Other' : issueType.charAt(0).toUpperCase() + issueType.slice(1);
    }
    return `Other: ${issueType}`;
  };

  return {
    ticketFormData,
    setTicketFormData,
    customIssueType,
    setCustomIssueType,
    ticketImageFiles,
    ticketImagePreviews,
    ticketImageNames,
    uploadingTicketImage,
    ticketLoading,
    loadingTickets,
    tickets,
    showTicketForm,
    setShowTicketForm,
    ticketMessage,
    setTicketMessage,
    loadTickets,
    getFilenameFromUrl,
    handleTicketImageChange,
    removeTicketImage,
    handleTicketSubmit,
    closeTicketForm,
    formatIssueType,
  };
}
